import {
  AddIcon,
  Button,
  Chip,
  DeleteIcon,
  Input,
  Modal,
  ShieldIcon,
  UpdateIcon,
} from "@bp/ui-components";
import { useState } from "react";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import styles from "./FederatedFormModal.module.scss";

const CreateFederatedClientsMutation = graphql(`
  mutation pimCreateFederatedClients($input: [FederatedClientCreateInput!]!) {
    createFederatedClients(input: $input) {
      federatedClients {
        uuid
      }
    }
  }
`);

const UpdateFederatedClientsMutation = graphql(`
  mutation pimUpdateFederatedClients($clientId: String!, $update: FederatedClientUpdateInput!) {
    updateFederatedClients(where: { clientId: $clientId }, update: $update) {
      federatedClients {
        uuid
      }
    }
  }
`);

export type FederatedLike = {
  name?: string | null;
  label?: string | null;
  issuer?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  responseTypes?: readonly string[] | null;
  customIdClaims?: readonly string[] | null;
};

type Props =
  | { mode: "create"; fed?: undefined; onClose: () => void; onSaved: () => void }
  | { mode: "edit"; fed: FederatedLike; onClose: () => void; onSaved: () => void };

const FED_RESPONSE_TYPES = ["code", "id_token", "code id_token"];

const genSecret = () => {
  const cryptoObj = globalThis.crypto;
  if (!cryptoObj?.getRandomValues) {
    throw new Error("Secure random generator unavailable — cannot mint a client secret.");
  }
  const bytes = new Uint8Array(24);
  cryptoObj.getRandomValues(bytes);
  return `cs_fed_${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
};

export function FederatedFormModal(props: Props) {
  const { mode, onClose, onSaved } = props;
  const fed = mode === "edit" ? props.fed : undefined;

  const [name, setName] = useState(fed?.name ?? "");
  const [label, setLabel] = useState(fed?.label ?? "");
  const [issuer, setIssuer] = useState(fed?.issuer ?? "");
  const [clientId, setClientId] = useState(fed?.clientId ?? "");
  const [clientSecret, setClientSecret] = useState(
    mode === "create" ? genSecret() : (fed?.clientSecret ?? ""),
  );
  const [responseTypes, setResponseTypes] = useState<string[]>(
    fed?.responseTypes ? [...fed.responseTypes] : ["code"],
  );
  const [claims, setClaims] = useState<string[]>(
    fed?.customIdClaims?.length ? [...fed.customIdClaims] : [""],
  );
  const [busy, setBusy] = useState(false);

  const [, createFed] = useMutation(CreateFederatedClientsMutation);
  const [, updateFed] = useMutation(UpdateFederatedClientsMutation);

  // edit keys the where-clause on the original clientId — block if it's missing,
  // otherwise the update would target where:{clientId:""} (wrong/zero rows).
  const valid =
    name.trim() &&
    issuer.trim() &&
    clientId.trim() &&
    (mode === "create" || Boolean(fed?.clientId));

  const toggleResponseType = (v: string) =>
    setResponseTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const setClaimAt = (i: number, v: string) =>
    setClaims((prev) => prev.map((c, j) => (j === i ? v : c)));
  const removeClaimAt = (i: number) =>
    setClaims((prev) => {
      const next = prev.filter((_, j) => j !== i);
      return next.length ? next : [""];
    });

  const save = async () => {
    setBusy(true);
    const claimList = claims.map((c) => c.trim()).filter(Boolean);
    const shared = {
      name: name.trim(),
      label: label.trim(),
      issuer: issuer.trim(),
      responseTypes,
      ...(clientId.trim() ? { clientId: clientId.trim() } : {}),
      ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}),
      ...(claimList.length ? { customIdClaims: claimList } : {}),
    };
    try {
      if (mode === "create") {
        const res = await createFed({ input: [shared] });
        if (res.error) {
          throw res.error;
        }
        showSuccessToast(`Föderation „${name}" angelegt`);
        onSaved();
      } else {
        const res = await updateFed({ clientId: fed?.clientId ?? "", update: shared });
        if (res.error) {
          throw res.error;
        }
        showSuccessToast(`Föderation „${name}" gespeichert`);
        onSaved();
      }
    } catch (error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error });
      setBusy(false);
    }
  };

  const claimRows = claims.length ? claims : [""];

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      width="s"
      hideFooter={false}
      title={
        mode === "create" ? "Föderierte Anbindung hinzufügen" : "Föderierte Anbindung bearbeiten"
      }
      onRequestClose={onClose}
      footer={
        <div className={styles.foot}>
          <span className={styles.footHint}>
            Externer OpenID-Provider, mit dem der Auth-Server föderiert.
          </span>
          <div className={styles.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={!valid || busy} onClick={() => void save()}>
              {mode === "create" ? "Hinzufügen" : "Speichern"}
            </Button>
          </div>
        </div>
      }
    >
      <div className={styles.stack}>
        <div className={styles.fld}>
          <span className={styles.fldLabel}>Name</span>
          <Input
            name="fedName"
            value={name}
            placeholder="z. B. Microsoft Entra — Stadtpark"
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className={styles.grid2}>
          <div className={styles.fld}>
            <span className={styles.fldLabel}>Label (Login-Button)</span>
            <Input
              name="fedLabel"
              value={label}
              placeholder="z. B. Microsoft Entra"
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className={styles.fld}>
            <span className={styles.fldLabel}>Protokoll</span>
            <Input name="fedProtocol" value="OpenID Connect" readonly disabled />
          </div>
        </div>

        <div className={styles.fld}>
          <span className={styles.fldLabel}>Issuer</span>
          <Input
            name="fedIssuer"
            className={styles.mono}
            value={issuer}
            placeholder="https://login.microsoftonline.com/<tenant>/v2.0"
            onChange={(e) => setIssuer(e.target.value)}
          />
        </div>

        <div className={styles.grid2}>
          <div className={styles.fld}>
            <span className={styles.fldLabel}>ClientId</span>
            <Input
              name="fedClientId"
              className={styles.mono}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>
          <div className={styles.fld}>
            <span className={styles.fldLabel}>Client Secret</span>
            <div className={styles.secretBox}>
              <Input
                name="fedClientSecret"
                className={styles.mono}
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
              />
              <Button
                hierarchy="tertiary"
                icon={<UpdateIcon />}
                title="Neu generieren"
                ariaLabel="Neu generieren"
                onClick={() => setClientSecret(genSecret())}
              />
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.fld}>
          <span className={styles.fldLabel}>Response Types</span>
          <div className={styles.fchips}>
            {FED_RESPONSE_TYPES.map((rt) => (
              <button
                key={rt}
                type="button"
                className={styles.chipBtn}
                aria-pressed={responseTypes.includes(rt)}
                onClick={() => toggleResponseType(rt)}
              >
                <Chip value={rt} active={responseTypes.includes(rt)} isSmall />
              </button>
            ))}
          </div>
        </div>

        <div className={styles.fld}>
          <span className={styles.fldLabel}>Custom ID Claims</span>
          <span className={styles.fldHint}>
            Zusätzliche Claims aus dem ID-Token, die als Identitäts-Merkmal übernommen werden.
          </span>
          <div className={styles.stackSm}>
            {claimRows.map((claim, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: editable free-text rows
              <div key={i} className={styles.claimRow}>
                <span className={styles.claimIcon}>
                  <ShieldIcon />
                </span>
                <Input
                  name={`fedClaim-${i}`}
                  className={styles.mono}
                  value={claim}
                  placeholder="z. B. oid, preferred_username"
                  onChange={(e) => setClaimAt(i, e.target.value)}
                />
                <Button
                  hierarchy="tertiary"
                  icon={<DeleteIcon />}
                  title="Entfernen"
                  ariaLabel="Entfernen"
                  disabled={claimRows.length === 1 && !claim}
                  onClick={() => removeClaimAt(i)}
                />
              </div>
            ))}
          </div>
          <Button
            className={styles.addBtn}
            hierarchy="tertiary"
            icon={<AddIcon />}
            onClick={() => setClaims((prev) => [...(prev.length ? prev : [""]), ""])}
          >
            Eintrag hinzufügen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
